import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Button,
  DatePicker,
  Empty,
  Input,
  Modal,
  Popconfirm,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from "antd";
import dayjs, { Dayjs } from "dayjs";
import { PrinterOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { financeService } from "../../services/financeService";
import { formatLkr } from "../../utils/currency";
import { hasAnyPermission, hasPermission } from "../../utils/permissions";
import type {
  BankAccount,
  PurchaseDetail,
  PurchaseList,
  VoucherJournalLine,
} from "../../types";
import { StatusTag } from "./VouchersPage";
import PurchaseFormModal from "./PurchaseFormModal";

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

type PurchaseFull = PurchaseDetail & { journal_lines?: VoucherJournalLine[] };

const STATUS_OPTIONS = [
  { value: "draft", label: "Draft" },
  { value: "pending_approval", label: "Pending approval" },
  { value: "approved", label: "Approved" },
  { value: "rejected", label: "Rejected" },
  { value: "paid", label: "Paid" },
  { value: "void", label: "Voided" },
];

/**
 * Purchase invoices — the documents that put goods on the shelf and a
 * credit in GL 1200.
 *
 * The workflow is the ordinary voucher workflow (thresholds, segregation of
 * duties, void-as-reversal) with two extras the operator can see: approving
 * RECEIVES the stock, and voiding sends it back to the supplier. The actions
 * offered are derived from the status so the button that cannot succeed is
 * never shown to be pressed.
 */
const PurchasesPage: React.FC = () => {
  const navigate = useNavigate();

  const canEdit = hasAnyPermission("finance:expense");
  const canApprove = hasPermission("finance:approve");

  const [status, setStatus] = useState<string | undefined>();
  const [range, setRange] = useState<[Dayjs, Dayjs] | null>(null);
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);

  const [data, setData] = useState<PurchaseList | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<PurchaseDetail | null>(null);
  const [detail, setDetail] = useState<PurchaseFull | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [printingId, setPrintingId] = useState<string | null>(null);
  const [payFor, setPayFor] = useState<PurchaseDetail | null>(null);
  const [payBank, setPayBank] = useState<string | undefined>();
  const [banks, setBanks] = useState<BankAccount[]>([]);
  const [paying, setPaying] = useState(false);
  const [voidFor, setVoidFor] = useState<PurchaseDetail | null>(null);
  const [voidReason, setVoidReason] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await financeService.getPurchases({
        status,
        from: range ? range[0].format("YYYY-MM-DD") : undefined,
        to: range ? range[1].format("YYYY-MM-DD") : undefined,
        q: q || undefined,
        page,
        limit: 20,
      });
      setData(res);
    } catch (e: any) {
      setError(e?.message || "Failed to load purchase invoices");
    } finally {
      setLoading(false);
    }
  }, [status, range, q, page]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    financeService
      .getBankAccounts()
      .then((rows) => setBanks(rows.filter((b) => b.is_active && b.type !== "card")))
      .catch(() => setBanks([]));
  }, []);

  const runAction = async (label: string, action: () => Promise<any>) => {
    try {
      await action();
      message.success(label);
      await load();
    } catch (e: any) {
      message.error(e?.message || `${label} failed`);
    }
  };

  const openDetail = async (row: PurchaseDetail) => {
    setDetailLoading(true);
    setDetail(null);
    try {
      setDetail((await financeService.getPurchase(row.id)) as PurchaseFull);
    } catch (e: any) {
      message.error(e?.message || "Failed to load purchase invoice");
    } finally {
      setDetailLoading(false);
    }
  };

  /**
   * Render the A4 order form on the server and open it in a new tab. If the
   * browser blocks the tab, the same PDF is downloaded so the operator is
   * never left without the document.
   */
  const printOrderForm = async (row: PurchaseDetail) => {
    setPrintingId(row.id);
    try {
      const blob = await financeService.getPurchaseOrderForm(row.id);
      const opened = financeService.openPdf(blob, `Order Form — ${row.voucher_no}`);
      if (!opened) {
        financeService.downloadPdf(blob, `order-form-${row.voucher_no}.pdf`);
        message.info("Pop-up blocked — the order form was downloaded instead");
      }
    } catch (e: any) {
      message.error(e?.message || "Failed to generate order form");
    } finally {
      setPrintingId(null);
    }
  };

  const submitPayment = async () => {
    if (!payFor || !payBank) return;
    setPaying(true);
    try {
      await financeService.payPurchase(payFor.id, payBank);
      message.success("Supplier payment recorded and posted");
      setPayFor(null);
      setPayBank(undefined);
      await load();
    } catch (e: any) {
      message.error(e?.message || "Payment failed");
    } finally {
      setPaying(false);
    }
  };

  const submitVoid = async () => {
    if (!voidFor) return;
    if (!voidReason.trim()) {
      message.error("A void reason is required");
      return;
    }
    try {
      await financeService.voidPurchase(voidFor.id, voidReason.trim());
      message.success("Purchase invoice voided — goods returned to supplier");
      setVoidFor(null);
      setVoidReason("");
      await load();
    } catch (e: any) {
      message.error(e?.message || "Void failed");
    }
  };

  const columns = [
    {
      title: "Invoice",
      dataIndex: "voucher_no",
      key: "voucher_no",
      width: 150,
      render: (v: string, row: PurchaseDetail) => (
        <Button type="link" size="small" style={{ padding: 0 }} onClick={() => openDetail(row)}>
          {v}
        </Button>
      ),
    },
    {
      title: "Date",
      dataIndex: "business_date",
      key: "business_date",
      width: 115,
      render: (v: string) => dayjs(v).format("MMM D, YYYY"),
    },
    {
      title: "Supplier",
      dataIndex: "party",
      key: "party",
      width: 180,
      ellipsis: true,
      render: (v: string | null) => v || "—",
    },
    {
      title: "Reference",
      dataIndex: "reference_no",
      key: "reference_no",
      width: 140,
      ellipsis: true,
      render: (v: string | null) => v || "—",
    },
    {
      title: "Department",
      dataIndex: "department_name",
      key: "department_name",
      width: 140,
      ellipsis: true,
      render: (v: string | null) => v || "—",
    },
    {
      title: "Items",
      key: "lines",
      width: 70,
      align: "right" as const,
      render: (_: any, row: PurchaseDetail) => row.lines?.length ?? "—",
    },
    {
      title: "Net",
      dataIndex: "amount",
      key: "amount",
      width: 130,
      align: "right" as const,
      render: (v: number) => formatLkr(v),
    },
    {
      title: "Tax",
      dataIndex: "tax",
      key: "tax",
      width: 120,
      align: "right" as const,
      render: (v: number) => formatLkr(v),
    },
    {
      title: "Total",
      key: "total",
      width: 140,
      align: "right" as const,
      render: (_: any, row: PurchaseDetail) => (
        <Text strong>{formatLkr(row.amount + row.tax)}</Text>
      ),
    },
    {
      title: "Paid by",
      dataIndex: "payment_method",
      key: "payment_method",
      width: 110,
      render: (v: string) => <Tag>{v === "credit" ? "Credit" : v.replace("_", " ")}</Tag>,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 150,
      render: (s: string, row: PurchaseDetail) => (
        <Space size={4}>
          <StatusTag status={s} />
          {s === "pending_approval" && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              {row.approvals_count}/{row.approvals_required}
            </Text>
          )}
        </Space>
      ),
    },
    {
      title: "",
      key: "actions",
      width: 250,
      render: (_: any, row: PurchaseDetail) => (
        <Space size={4} wrap>
          <Button size="small" onClick={() => openDetail(row)}>
            View
          </Button>
          <Button
            size="small"
            icon={<PrinterOutlined />}
            loading={printingId === row.id}
            onClick={() => printOrderForm(row)}
          >
            Print
          </Button>
          {(row.status === "draft" || row.status === "rejected") && canEdit && (
            <Button
              size="small"
              onClick={() => {
                setEditing(row);
                setFormOpen(true);
              }}
            >
              Edit
            </Button>
          )}
          {(row.status === "draft" || row.status === "rejected") && canEdit && (
            <Popconfirm
              title="Submit for approval?"
              onConfirm={() =>
                runAction("Submitted for approval", () => financeService.submitPurchase(row.id))
              }
            >
              <Button size="small" type="primary">
                Submit
              </Button>
            </Popconfirm>
          )}
          {row.status === "draft" && canEdit && (
            <Popconfirm
              title="Delete this draft?"
              onConfirm={() =>
                runAction("Draft deleted", () => financeService.deletePurchase(row.id))
              }
            >
              <Button size="small" danger>
                Delete
              </Button>
            </Popconfirm>
          )}
          {row.status === "pending_approval" && canApprove && (
            <Popconfirm
              title="Approve — goods will be received into stock"
              onConfirm={() =>
                runAction("Approved — goods received", () =>
                  financeService.approvePurchase(row.id),
                )
              }
            >
              <Button size="small" type="primary">
                Approve
              </Button>
            </Popconfirm>
          )}
          {row.status === "pending_approval" && canApprove && (
            <Popconfirm
              title="Reject this invoice?"
              onConfirm={() =>
                runAction("Purchase invoice rejected", () =>
                  financeService.rejectPurchase(row.id, "Rejected from the purchase register"),
                )
              }
            >
              <Button size="small" danger>
                Reject
              </Button>
            </Popconfirm>
          )}
          {row.status === "approved" && row.payment_method === "credit" && canEdit && (
            <Button
              size="small"
              onClick={() => {
                setPayFor(row);
                setPayBank(undefined);
              }}
            >
              Pay
            </Button>
          )}
          {["pending_approval", "approved", "paid", "posted"].includes(row.status) && canEdit && (
            <Button
              size="small"
              danger
              onClick={() => {
                setVoidFor(row);
                setVoidReason("");
              }}
            >
              Void
            </Button>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Space
        style={{ width: "100%", justifyContent: "space-between", marginBottom: 16 }}
        wrap
      >
        <div>
          <Title level={4} style={{ margin: 0 }}>
            Purchase invoices
          </Title>
          <Text type="secondary">
            {data ? `${data.total} invoice(s)` : "Loading…"}
          </Text>
        </div>
        <Space wrap>
          {canEdit && (
            <Button
              type="primary"
              onClick={() => {
                setEditing(null);
                setFormOpen(true);
              }}
            >
              New purchase invoice
            </Button>
          )}
          <Button onClick={load}>Refresh</Button>
          <Button onClick={() => navigate("/finance/inventory")}>Stock &amp; recipes</Button>
        </Space>
      </Space>

      <Space wrap style={{ marginBottom: 16 }}>
        <Select
          allowClear
          placeholder="Any status"
          style={{ width: 190 }}
          value={status}
          onChange={(v) => {
            setPage(1);
            setStatus(v);
          }}
          options={STATUS_OPTIONS}
        />
        <RangePicker
          value={range}
          onChange={(dates) => {
            setPage(1);
            setRange(dates && dates[0] && dates[1] ? [dates[0], dates[1]] : null);
          }}
        />
        <Input.Search
          allowClear
          placeholder="Search supplier / reference / description"
          style={{ width: 300 }}
          onSearch={(value) => {
            setPage(1);
            setQ(value);
          }}
        />
      </Space>

      {error && (
        <Alert
          type="error"
          showIcon
          title="Failed to load"
          description={error}
          style={{ marginBottom: 16 }}
        />
      )}

      <Table
        rowKey="id"
        columns={columns}
        dataSource={data?.rows ?? []}
        loading={loading}
        scroll={{ x: 1500 }}
        size="small"
        pagination={{
          current: page,
          pageSize: 20,
          total: data?.total ?? 0,
          showSizeChanger: false,
          onChange: setPage,
          showTotal: (t) => `${t} invoices`,
        }}
        locale={{
          emptyText: (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No purchase invoices yet" />
          ),
        }}
      />

      {/* ---- Detail ---- */}
      <Modal
        open={!!detail || detailLoading}
        title={detail ? `${detail.voucher_no} — ${detail.party || "Supplier"}` : "Purchase invoice"}
        footer={null}
        width={860}
        onCancel={() => setDetail(null)}
        destroyOnClose
      >
        {detailLoading && <Empty description="Loading…" />}
        {detail && (
          <>
            <Space style={{ width: "100%", justifyContent: "flex-end", marginBottom: 8 }}>
              <Button
                icon={<PrinterOutlined />}
                loading={printingId === detail.id}
                onClick={() => printOrderForm(detail)}
              >
                Print Order Form
              </Button>
            </Space>
            <Space direction="vertical" style={{ width: "100%" }} size={4}>
              <Space wrap size={16}>
                <StatusTag status={detail.status} />
                <Text type="secondary">Dated {dayjs(detail.business_date).format("MMM D, YYYY")}</Text>
                <Text type="secondary">{detail.department_name}</Text>
                <Text type="secondary">Paid by {detail.payment_method.replace("_", " ")}</Text>
                <Text type="secondary">
                  Ordered by {detail.ordered_by_name || detail.created_by_name || "—"}
                </Text>
                {detail.reference_no && <Text type="secondary">Ref {detail.reference_no}</Text>}
              </Space>
              <Text>{detail.description}</Text>
              <Space wrap size={16}>
                <Text>
                  Net <Text strong>{formatLkr(detail.amount)}</Text>
                </Text>
                <Text>
                  Tax <Text strong>{formatLkr(detail.tax)}</Text>
                </Text>
                <Text>
                  Total <Text strong>{formatLkr(detail.amount + detail.tax)}</Text>
                </Text>
                <Text type="secondary">
                  {detail.approvals_count}/{detail.approvals_required} approval(s)
                </Text>
              </Space>
              {detail.paid_at && (
                <Text type="secondary">
                  Supplier paid {dayjs(detail.paid_at).format("MMM D, YYYY")}
                </Text>
              )}
            </Space>

            <Title level={5} style={{ marginTop: 20, marginBottom: 8 }}>
              Stock received
            </Title>
            <Table
              rowKey="id"
              size="small"
              pagination={false}
              dataSource={detail.lines || []}
              columns={[
                { title: "Code", dataIndex: "item_code", key: "item_code", width: 90 },
                { title: "Item", dataIndex: "item_name", key: "item_name" },
                { title: "Unit", dataIndex: "unit", key: "unit", width: 70 },
                {
                  title: "Qty",
                  dataIndex: "quantity",
                  key: "quantity",
                  width: 90,
                  align: "right" as const,
                },
                {
                  title: "Unit cost",
                  dataIndex: "unit_cost",
                  key: "unit_cost",
                  width: 130,
                  align: "right" as const,
                  render: (v: number) => formatLkr(v),
                },
                {
                  title: "Line total",
                  dataIndex: "line_total",
                  key: "line_total",
                  width: 140,
                  align: "right" as const,
                  render: (v: number) => formatLkr(v),
                },
              ]}
              locale={{ emptyText: <Empty description="No lines" /> }}
            />

            <Title level={5} style={{ marginTop: 20, marginBottom: 8 }}>
              Ledger entries
            </Title>
            {detail.journal_lines?.length ? (
              <Table
                rowKey="id"
                size="small"
                pagination={false}
                dataSource={detail.journal_lines}
                columns={[
                  {
                    title: "Account",
                    key: "account",
                    render: (_: any, l: VoucherJournalLine) => (
                      <Text code>{l.account_code}</Text>
                    ),
                  },
                  { title: "Name", dataIndex: "account_name", key: "account_name" },
                  { title: "Side", dataIndex: "side", key: "side", width: 90 },
                  {
                    title: "Amount",
                    dataIndex: "amount",
                    key: "amount",
                    width: 150,
                    align: "right" as const,
                    render: (v: number) => formatLkr(v),
                  },
                ]}
              />
            ) : (
              <Alert
                type="info"
                showIcon
                title="Not posted yet"
                description="The journal is written when the approval chain completes. Until then there is nothing in the ledger to show — and nothing has been assumed."
              />
            )}
          </>
        )}
      </Modal>

      {/* ---- Pay ---- */}
      <Modal
        open={!!payFor}
        title="Record supplier payment"
        onCancel={() => setPayFor(null)}
        onOk={submitPayment}
        okText="Record payment"
        confirmLoading={paying}
        destroyOnClose
      >
        <Space direction="vertical" style={{ width: "100%" }}>
          <Text>
            {payFor?.voucher_no} — <Text strong>{formatLkr((payFor?.amount || 0) + (payFor?.tax || 0))}</Text> to{" "}
            {payFor?.party || "the supplier"}
          </Text>
          <Select
            style={{ width: "100%" }}
            placeholder="Where the money leaves from"
            value={payBank}
            onChange={setPayBank}
            options={banks.map((b) => ({
              value: b.id,
              label: `${b.name} (${b.type})`,
            }))}
          />
        </Space>
      </Modal>

      {/* ---- Void ---- */}
      <Modal
        open={!!voidFor}
        title="Void purchase invoice"
        onCancel={() => setVoidFor(null)}
        onOk={submitVoid}
        okText="Void invoice"
        okButtonProps={{ danger: true }}
        destroyOnClose
      >
        <Alert
          type="warning"
          showIcon
          title="This reverses the journal and returns the goods to the supplier"
          description="The receipt movements are compensated (the history stays, with the reason beside them) and GL 1200 falls back by the same amount. If the invoice was already paid, the settlement is reversed first."
          style={{ marginBottom: 16 }}
        />
        <Input.TextArea
          rows={3}
          placeholder="Why is this being voided?"
          value={voidReason}
          onChange={(e) => setVoidReason(e.target.value)}
        />
      </Modal>

      <PurchaseFormModal
        open={formOpen}
        editing={editing}
        onClose={() => {
          setFormOpen(false);
          setEditing(null);
        }}
        onSaved={() => {
          message.success(editing ? "Purchase invoice updated" : "Purchase invoice created");
          setFormOpen(false);
          setEditing(null);
          load();
        }}
      />
    </div>
  );
};

export default PurchasesPage;
